import { Link } from "@tanstack/react-router";
import {
	CalendarClock,
	Check,
	ChevronDown,
	PenLine,
	Plug,
	X,
} from "lucide-react";
import * as React from "react";
import { useDashboardContext } from "@/components/dashboard/DashboardContext";
import { fireGoldenConfetti } from "@/lib/confetti";
import { trackGa4EventOnce } from "@/lib/ga4";
import { db } from "@/lib/instantdb";
import { cn } from "@/lib/utils";

type StepId =
	| "account_connected"
	| "first_draft_created"
	| "first_post_scheduled";
type OnboardingStatus =
	| "workspace_created"
	| "account_connected"
	| "first_draft_created"
	| "first_post_scheduled"
	| "completed";

const STEPS: Array<{
	id: StepId;
	label: string;
	icon: React.ComponentType<{ className?: string }>;
	to?: "/dashboard/connect" | "/dashboard/chat" | "/dashboard/posts";
}> = [
	{
		id: "account_connected",
		label: "Account connected",
		icon: Plug,
		to: "/dashboard/connect",
	},
	{
		id: "first_draft_created",
		label: "First draft created",
		icon: PenLine,
		to: "/dashboard/chat",
	},
	{
		id: "first_post_scheduled",
		label: "First post scheduled",
		icon: CalendarClock,
		to: "/dashboard/posts",
	},
];

const ONBOARDING_STATUS_RANK: Record<OnboardingStatus, number> = {
	workspace_created: 0,
	account_connected: 1,
	first_draft_created: 2,
	first_post_scheduled: 3,
	completed: 4,
};

const dismissKey = (workspaceId: string) =>
	`ar:onboardingChecklistDismissed:${workspaceId}`;
const collapseKey = (workspaceId: string) =>
	`ar:onboardingChecklistCollapsed:${workspaceId}`;
const completedConfettiKey = (workspaceId: string) =>
	`ar:onboardingCompletedConfetti:${workspaceId}`;

function isOnboardingStatus(
	value: string | null | undefined,
): value is OnboardingStatus {
	return Boolean(value && value in ONBOARDING_STATUS_RANK);
}

async function updateWorkspaceOnboardingStatus(
	workspaceId: string,
	onboardingStatus: OnboardingStatus,
) {
	const response = await fetch("/api/workspaces", {
		method: "PATCH",
		headers: {
			Accept: "application/json",
			"Content-Type": "application/json",
		},
		body: JSON.stringify({ workspaceId, onboardingStatus }),
	});
	if (!response.ok) {
		throw new Error("Failed to update onboarding status");
	}
}

function checklistOrigin() {
	if (typeof window === "undefined") return { x: 0.85, y: 0.9 };
	const el = document.querySelector<HTMLElement>(
		'[data-onboarding-checklist="true"]',
	);
	if (!el) return { x: 0.85, y: 0.9 };
	const rect = el.getBoundingClientRect();
	const x = (rect.left + rect.width / 2) / window.innerWidth;
	const y = (rect.top + rect.height / 2) / window.innerHeight;
	return { x, y };
}

export function OnboardingChecklist() {
	const { workspace } = useDashboardContext();
	const workspaceId = workspace?.id;

	const { data: channelsData } = db.useQuery(
		workspaceId
			? {
					socialChannels: {
						$: {
							where: { "workspace.id": workspaceId, status: "connected" },
							limit: 1,
						},
					},
				}
			: null,
	);

	const { data: draftsData } = db.useQuery(
		workspaceId
			? {
					contentPosts: {
						$: { where: { ownerKey: workspaceId }, limit: 1 },
					},
				}
			: null,
	);

	const { data: publishedData } = db.useQuery(
		workspaceId
			? {
					contentPosts: {
						$: {
							where: { ownerKey: workspaceId, status: "published" },
							limit: 1,
						},
					},
				}
			: null,
	);

	const { data: scheduledData } = db.useQuery(
		workspaceId
			? {
					contentPosts: {
						$: {
							where: { ownerKey: workspaceId, status: "scheduled" },
							limit: 1,
						},
					},
				}
			: null,
	);

	const hasConnected = (channelsData?.socialChannels?.length ?? 0) > 0;
	const hasDrafted = (draftsData?.contentPosts?.length ?? 0) > 0;
	const hasScheduled = (scheduledData?.contentPosts?.length ?? 0) > 0;
	const hasPublished = (publishedData?.contentPosts?.length ?? 0) > 0;
	const hasScheduledOrPublished = hasScheduled || hasPublished;
	const currentRank =
		ONBOARDING_STATUS_RANK[
			isOnboardingStatus(workspace?.onboardingStatus)
				? workspace.onboardingStatus
				: "workspace_created"
		];

	const completion: Record<StepId, boolean> = {
		account_connected:
			hasConnected || currentRank >= ONBOARDING_STATUS_RANK.account_connected,
		first_draft_created:
			hasDrafted || currentRank >= ONBOARDING_STATUS_RANK.first_draft_created,
		first_post_scheduled:
			hasScheduledOrPublished ||
			currentRank >= ONBOARDING_STATUS_RANK.first_post_scheduled,
	};
	const doneCount = Object.values(completion).filter(Boolean).length;
	const nextStepId = STEPS.find((s) => !completion[s.id])?.id ?? null;

	const [dismissed, setDismissed] = React.useState<boolean>(() => {
		if (typeof window === "undefined" || !workspaceId) return false;
		return window.localStorage.getItem(dismissKey(workspaceId)) === "1";
	});
	const [collapsed, setCollapsed] = React.useState<boolean>(() => {
		if (typeof window === "undefined" || !workspaceId) return false;
		return window.localStorage.getItem(collapseKey(workspaceId)) === "1";
	});
	React.useEffect(() => {
		if (!workspaceId || typeof window === "undefined") return;
		setDismissed(window.localStorage.getItem(dismissKey(workspaceId)) === "1");
		setCollapsed(window.localStorage.getItem(collapseKey(workspaceId)) === "1");
	}, [workspaceId]);

	const statusUpdateRef = React.useRef<string | null>(null);
	React.useEffect(() => {
		if (!workspaceId || typeof window === "undefined") return;
		if (!channelsData || !draftsData || !publishedData || !scheduledData) {
			return;
		}

		let nextStatus: OnboardingStatus | null = null;

		if (
			currentRank < ONBOARDING_STATUS_RANK.account_connected &&
			hasConnected
		) {
			nextStatus = "account_connected";
		} else if (
			currentRank < ONBOARDING_STATUS_RANK.first_draft_created &&
			hasDrafted
		) {
			nextStatus = "first_draft_created";
		} else if (
			currentRank < ONBOARDING_STATUS_RANK.completed &&
			hasScheduledOrPublished
		) {
			nextStatus = "completed";
		}

		if (!nextStatus) return;

		const updateKey = `${workspaceId}:${nextStatus}`;
		if (statusUpdateRef.current === updateKey) return;
		statusUpdateRef.current = updateKey;

		updateWorkspaceOnboardingStatus(workspaceId, nextStatus)
			.then(() => {
				if (nextStatus !== "completed") return;
				const key = completedConfettiKey(workspaceId);
				if (window.localStorage.getItem(key) === "1") return;
				window.localStorage.setItem(key, "1");
				fireGoldenConfetti({ origin: checklistOrigin() });
			})
			.catch(() => {
				statusUpdateRef.current = null;
			});
	}, [
		workspaceId,
		currentRank,
		channelsData,
		draftsData,
		publishedData,
		scheduledData,
		hasConnected,
		hasDrafted,
		hasScheduledOrPublished,
	]);

	React.useEffect(() => {
		if (!workspaceId) return;
		if (!channelsData || !draftsData || !publishedData || !scheduledData) {
			return;
		}

		const eventParams = { workspace_id: workspaceId };

		if (hasConnected) {
			trackGa4EventOnce(
				`connected_account:${workspaceId}`,
				"connected_account",
				eventParams,
			);
			trackGa4EventOnce(`activation:${workspaceId}`, "activation", {
				...eventParams,
				activation_type: "connected_account",
			});
		}

		if (hasDrafted) {
			trackGa4EventOnce(
				`first_draft:${workspaceId}`,
				"first_draft",
				eventParams,
			);
		}

		if (hasScheduled) {
			trackGa4EventOnce(
				`first_scheduled_post:${workspaceId}`,
				"first_scheduled_post",
				eventParams,
			);
		}

		if (hasPublished) {
			trackGa4EventOnce(
				`first_published_post:${workspaceId}`,
				"first_published_post",
				eventParams,
			);
		}
	}, [
		workspaceId,
		channelsData,
		draftsData,
		publishedData,
		scheduledData,
		hasConnected,
		hasDrafted,
		hasScheduled,
		hasPublished,
	]);

	if (!workspaceId) return null;
	if (doneCount === STEPS.length) return null;
	if (dismissed) return null;

	const handleDismiss = () => {
		setDismissed(true);
		window.localStorage.setItem(dismissKey(workspaceId), "1");
	};

	const handleToggleCollapse = () => {
		const next = !collapsed;
		setCollapsed(next);
		window.localStorage.setItem(collapseKey(workspaceId), next ? "1" : "0");
	};

	const nextStep = STEPS.find((step) => step.id === nextStepId) ?? null;

	return (
		<>
			<div
				data-onboarding-checklist="true"
				className="fixed bottom-4 right-4 z-40 hidden w-[22rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-lg sm:block"
			>
				<header className="flex items-center justify-between gap-2 px-4 py-3">
					<button
						type="button"
						onClick={handleToggleCollapse}
						className="flex flex-1 items-center gap-2 text-left"
						aria-expanded={!collapsed}
					>
						<span className="text-sm font-semibold">Get started</span>
						<span className="text-xs text-muted-foreground">
							{doneCount} of {STEPS.length} done
						</span>
						<ChevronDown
							className={cn(
								"ml-auto size-4 text-muted-foreground transition-transform",
								collapsed && "-rotate-90",
							)}
						/>
					</button>
					<button
						type="button"
						onClick={handleDismiss}
						aria-label="Dismiss onboarding checklist"
						className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
					>
						<X className="size-4" />
					</button>
				</header>

				{!collapsed && (
					<>
						<div className="h-1 w-full bg-muted">
							<div
								className="h-full bg-primary transition-[width]"
								style={{ width: `${(doneCount / STEPS.length) * 100}%` }}
							/>
						</div>
						<ol className="flex flex-col gap-1 p-2">
							{STEPS.map((step) => {
								const done = completion[step.id];
								const isNext = !done && step.id === nextStepId;
								const content = (
									<>
										<span
											className={cn(
												"flex size-8 shrink-0 items-center justify-center rounded-full",
												done && "bg-primary text-primary-foreground",
												isNext &&
													"border-2 border-primary bg-background text-primary",
												!done &&
													!isNext &&
													"border border-border bg-muted/40 text-muted-foreground",
											)}
										>
											{done ? (
												<Check className="size-4" />
											) : (
												<step.icon className="size-4" />
											)}
										</span>
										<span
											className={cn(
												"text-sm",
												done
													? "text-muted-foreground line-through"
													: isNext
														? "font-medium text-foreground"
														: "text-muted-foreground",
											)}
										>
											{step.label}
										</span>
									</>
								);
								const itemClassName = cn(
									"flex items-center gap-3 rounded-lg px-2 py-2 transition-colors",
									"hover:bg-muted",
								);

								return (
									<li key={step.id}>
										{step.to ? (
											<Link to={step.to} className={itemClassName}>
												{content}
											</Link>
										) : null}
									</li>
								);
							})}
						</ol>
					</>
				)}
			</div>

			<div className="fixed inset-x-3 top-[calc(env(safe-area-inset-top)+4.5rem)] z-40 overflow-hidden rounded-xl border border-border bg-popover/95 text-popover-foreground shadow-lg backdrop-blur sm:hidden">
				<div className="h-1 w-full bg-muted">
					<div
						className="h-full bg-primary transition-[width]"
						style={{ width: `${(doneCount / STEPS.length) * 100}%` }}
					/>
				</div>
				<div className="flex items-center gap-3 p-3">
					<div className="min-w-0 flex-1">
						<div className="flex items-center gap-2">
							<p className="truncate text-sm font-semibold">Get started</p>
							<p className="shrink-0 text-xs text-muted-foreground">
								{doneCount}/{STEPS.length}
							</p>
						</div>
						{nextStep?.to ? (
							<Link
								to={nextStep.to}
								className="mt-2 flex min-h-10 items-center gap-2 rounded-lg bg-muted/50 px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted"
							>
								<span className="flex size-6 shrink-0 items-center justify-center rounded-full border-2 border-primary bg-background text-primary">
									<nextStep.icon className="size-3.5" />
								</span>
								<span className="min-w-0 flex-1 truncate">
									{nextStep.label}
								</span>
							</Link>
						) : null}
					</div>
					<button
						type="button"
						onClick={handleDismiss}
						aria-label="Dismiss onboarding checklist"
						className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
					>
						<X className="size-4" />
					</button>
				</div>
			</div>
		</>
	);
}
